import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseDateToParts, buildDateFromParts } from '@/utils/dateFormatting';
import type { DatePrecision } from '@/utils/dateFormatting';

import './date-field.css';

const MONTH_MIN = 1;
const MONTH_MAX = 12;
const DAY_MIN = 1;
const DAY_MAX = 31;
const YEAR_MIN = 1000;

function getYearMax(): number {
  return new Date().getFullYear();
}

export interface DatePartsInputProps {
  id: string;
  value: string;
  onChange: (canonicalDate: string) => void;
  /** Which parts to show: year only, month+year, or all three. Hidden parts keep their values. */
  precision: DatePrecision;
  disabled?: boolean;
  className?: string;
  'aria-describedby'?: string;
}

/** Normalize padded "01"/"15" to display form "1"/"15" for natural editing. */
function toDisplayPart(p: string): string {
  if (!p) return '';
  const n = parseInt(p, 10);
  return Number.isNaN(n) ? p : String(n);
}

/** Clamp numeric string to range; return empty string as-is. Only clamps when value is in range. */
function clampPart(value: string, min: number, max: number): string {
  if (value === '') return '';
  const n = parseInt(value, 10);
  if (Number.isNaN(n)) return value;
  return String(Math.min(max, Math.max(min, n)));
}

/** Clamp month/day when 2 digits; otherwise allow partial (e.g. "0" for "05"). */
function clampMonthDay(value: string, min: number, max: number): string {
  if (value.length < 2) return value;
  return clampPart(value, min, max);
}

/** Clamp year when 4 digits. */
function clampYear(value: string): string {
  if (value.length < 4) return value;
  return clampPart(value, YEAR_MIN, getYearMax());
}

/**
 * Same three inputs (month, day, year) in MM / DD / YYYY order. Parts that don't
 * apply for the current precision are hidden, not removed, so switching precision
 * keeps existing values. Month and day show as natural numbers (1–12, 1–31); no
 * leading zeros so typing and backspace feel natural. Composes to YYYY-MM-DD internally.
 */
const DatePartsInput: React.FC<DatePartsInputProps> = ({
  id,
  value,
  onChange,
  precision,
  disabled = false,
  className = '',
  'aria-describedby': ariaDescribedBy
}) => {
  const { t } = useTranslation('forms');
  const parts = parseDateToParts(value);
  const [year, setYear] = useState(parts.year);
  const [month, setMonth] = useState(toDisplayPart(parts.month));
  const [day, setDay] = useState(toDisplayPart(parts.day));

  useEffect(() => {
    const next = parseDateToParts(value);
    setYear(next.year);
    setMonth(toDisplayPart(next.month));
    setDay(toDisplayPart(next.day));
  }, [value]);

  const showMonth = precision === 'month' || precision === 'day';
  const showDay = precision === 'day';

  // Use empty string for hidden parts so buildDateFromParts applies correct defaults (01 for month/day).
  // This avoids stale state when switching precision (e.g. year precision must not use old month/day).
  const commitIfValid = (y: string, m: string, d: string) => {
    const effectiveMonth = showMonth ? m : '';
    const effectiveDay = showDay ? d : '';
    const canonical = buildDateFromParts(y, effectiveMonth, effectiveDay);
    if (canonical) onChange(canonical);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 2);
    const clamped = clampMonthDay(v, MONTH_MIN, MONTH_MAX);
    setMonth(clamped);
    commitIfValid(year, clamped, day);
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 2);
    const clamped = clampMonthDay(v, DAY_MIN, DAY_MAX);
    setDay(clamped);
    commitIfValid(year, month, clamped);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
    const clamped = clampYear(v);
    setYear(clamped);
    commitIfValid(clamped, month, day);
  };

  const handleMonthKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const n = parseInt(month || '1', 10) || 1;
    const delta = e.key === 'ArrowUp' ? 1 : -1;
    const next = clampPart(String(n + delta), MONTH_MIN, MONTH_MAX);
    if (next === '') return;
    setMonth(next);
    commitIfValid(year, next, day);
  };

  const handleDayKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const n = parseInt(day || '1', 10) || 1;
    const delta = e.key === 'ArrowUp' ? 1 : -1;
    const next = clampPart(String(n + delta), DAY_MIN, DAY_MAX);
    if (next === '') return;
    setDay(next);
    commitIfValid(year, month, next);
  };

  const handleYearKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const yMax = getYearMax();
    const n = parseInt(year || String(yMax), 10) || yMax;
    const delta = e.key === 'ArrowUp' ? 1 : -1;
    const next = clampPart(String(n + delta), YEAR_MIN, yMax);
    if (next === '') return;
    setYear(next);
    commitIfValid(next, month, day);
  };

  const yearId = precision === 'year' ? id : `${id}-year`;
  const monthId = precision === 'year' ? `${id}-month` : id;

  return (
    <div className={`date-field__date-parts ${className}`.trim()}>
      <input
        type="text"
        inputMode="numeric"
        id={monthId}
        placeholder={t('date.placeholderMonth')}
        maxLength={2}
        value={showMonth ? month : ''}
        onChange={handleMonthChange}
        onKeyDown={handleMonthKeyDown}
        disabled={disabled}
        tabIndex={showMonth ? undefined : -1}
        className={`date-field__input date-field__date-part date-field__date-part--month ${!showMonth ? 'date-field__date-part--hidden' : ''}`}
        aria-label={t('date.month')}
        aria-hidden={!showMonth}
        aria-describedby={ariaDescribedBy}
      />
      <input
        type="text"
        inputMode="numeric"
        id={`${id}-day`}
        placeholder={t('date.placeholderDay')}
        maxLength={2}
        value={showDay ? day : ''}
        onChange={handleDayChange}
        onKeyDown={handleDayKeyDown}
        disabled={disabled}
        tabIndex={showDay ? undefined : -1}
        className={`date-field__input date-field__date-part date-field__date-part--day ${!showDay ? 'date-field__date-part--hidden' : ''}`}
        aria-label={t('date.day')}
        aria-hidden={!showDay}
        aria-describedby={ariaDescribedBy}
      />
      <input
        type="text"
        inputMode="numeric"
        id={yearId}
        placeholder={t('date.placeholderYear')}
        maxLength={4}
        value={year}
        onChange={handleYearChange}
        onKeyDown={handleYearKeyDown}
        disabled={disabled}
        className="date-field__input date-field__date-part date-field__date-part--year"
        aria-label={t('date.year')}
        aria-describedby={ariaDescribedBy}
      />
    </div>
  );
};

export default DatePartsInput;
